import axios from "../plugins/axios";
import type { AxiosRequestConfig } from "axios";
import { saveAs } from "file-saver";

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

export type Absence = "required" | "recommended" | "optional";
export type PositionStatus = "draft" | "approved";
export type Severity = "critical" | "material" | "preference";
export type DocumentRole = "template" | "guidance" | "checklist" | "other";
export type GenerationState = "generating" | "complete" | "failed";

// The generation fields are absent on a playbook that was never AI-generated.
export interface PlaybookSummary {
    key: string;
    displayName: string;
    version: string;
    enabled: boolean;
    archived: boolean;
    provisionCount: number;
    draftCount: number;
    generationStatus?: GenerationState | null;
    generationProgress?: string | null;
    generationError?: string | null;
    generationStartedOn?: string | null;
    generationUpdatedOn?: string | null;
}

export interface PlaybookSource {
    id: number;
    fileName: string;
    role: DocumentRole;
    isPrimary: boolean;
    characters: number;
    uploadedOn: string;
    uploadedByName?: string | null;
}

export interface Position {
    status: PositionStatus;
    requirement: string | null;
    housePosition: string | null;
    unacceptable: string | null;
    absenceSeverity: Severity;
    citation: string | null;
    modelClause: string | null;
    approvedByName: string | null;
    approvedOn: string | null;
}

export interface Provision {
    key: string;
    name: string;
    description: string | null;
    absence: Absence;
    keywords: string[];
    // Set by the generator when a draft needs checking; it blocks approval until resolved.
    reviewNote?: string | null;
    // null is Ungrouped. Display only; reviews ignore both.
    groupId?: number | null;
    sortOrder: number;
    position: Position;
}

export interface PlaybookGroup {
    id: number;
    name: string;
    sortOrder: number;
}

// The whole layout: every group once in display order, every live provision once.
export interface Arrangement {
    groups: { id: number; provisionKeys: string[] }[];
    ungrouped: string[];
}

export interface ArchivedProvision {
    key: string;
    name: string;
    archivedOn: string;
}

export interface Playbook extends PlaybookSummary {
    groups: PlaybookGroup[];
    provisions: Provision[];
    archivedProvisions: ArchivedProvision[];
    sources: PlaybookSource[];
}

export interface PlaybookChange {
    changedOn: string;
    action: string;
    provisionKey: string | null;
    detail: string | null;
    byName: string | null;
    byEmail: string | null;
}

export interface SaveProvision {
    key: string;
    name: string;
    description: string;
    absence: Absence;
    keywords: string[];
    position: {
        // null leaves the stored status alone
        status?: PositionStatus | null;
        requirement: string;
        housePosition: string;
        unacceptable: string | null;
        absenceSeverity: Severity;
        citation: string;
        modelClause: string;
    };
    // Omitted or null leaves the note alone; "" clears it.
    reviewNote?: string | null;
    // null leaves the group alone (Ungrouped on create); 0 moves it to Ungrouped.
    groupId?: number | null;
}

export interface GeneratorDocument {
    name: string;
    role: DocumentRole;
    documentBase64?: string;
    text?: string;
}

export interface GenerateRequest {
    key: string;
    displayName: string;
    documents: GeneratorDocument[];
    // Index into documents of the primary precedent; required when more than one has text.
    primaryDocument?: number;
}

export interface GenerateResult {
    key: string;
    added: number;
    provisionKeys: string[];
    model: string | null;
    // Drafts before merging; mergedCount is the provisions merged from several drafts, flaggedCount those with a review note.
    draftedCount: number;
    mergedCount: number;
    flaggedCount: number;
    documentsRead: number;
    precedentCharacters: number;
}

export type RunSource = "upload" | "addin";
export type RunStatus = "running" | "complete" | "failed";
export type RedlineStatus = "ok" | "commentsonly" | "failed";
export type FindingType = "defect" | "omission" | "uncoveredsection";
export type FixMode = "replacesentence" | "appendsentence" | "insertclause";
export type FindingDecision = "undecided" | "accepted" | "declined";

// The API omits null fields, so anything nullable may also be absent.
export interface RunSummary {
    identifier: string;
    source: RunSource;
    startedOn: string;
    completedOn?: string | null;
    documentName: string;
    clientName?: string | null;
    isTest: boolean;
    // Absent on runs recorded before the requester was captured.
    requestedByName?: string | null;
    requestedByEmail?: string | null;
    playbookKey: string;
    playbookName: string;
    playbookVersion: string;
    status: RunStatus;
    error?: string | null;
    // The stage a running review has reached; absent once it completes or fails.
    progress?: string | null;
    progressUpdatedOn?: string | null;
    counts: {
        critical: number;
        material: number;
        preference: number;
        omissions: number;
        notAssessed: number;
        total: number;
    };
    durationMs?: number | null;
    hasRedline: boolean;
    redlineOn?: string | null;
    redlineStale: boolean;
}

export interface Finding {
    key: string;
    type: FindingType;
    provisionKey?: string | null;
    provisionName: string;
    severity: Severity;
    message?: string | null;
    citation?: string | null;
    evidence?: string | null;
    clientNote?: string | null;
    paragraphIndex?: number | null;
    heading?: string | null;
    fix?: { mode: FixMode; find?: string | null; text?: string | null } | null;
    decision: FindingDecision;
    decidedByName?: string | null;
    decidedOn?: string | null;
}

export interface Run extends RunSummary {
    model?: string | null;
    sectionCount: number;
    mappedProvisions: string[];
    redline: { status?: RedlineStatus | null; changes: number; error?: string | null };
    decisions: { accepted: number; declined: number; undecided: number; notAssessed: number };
    findings: Finding[];
}

// The reviewed document for the side-by-side view. number is the list label Word shows, when it could be worked out.
export interface RunDocumentParagraph {
    index: number;
    text: string;
    style: string;
    isHeading: boolean;
    number?: string | null;
}

// available is false for a Word add-in run, which stores no document.
export interface RunDocument {
    available: boolean;
    paragraphs: RunDocumentParagraph[];
}

export interface CreateRun {
    identifier: string;
    playbookKey: string;
    documentName: string;
    clientName?: string;
    isTest: boolean;
    documentBase64: string;
}

// The generation calls and the follow-up polling surface their own errors rather than the global snack.
const noSnack = { errorHandle: false } as AxiosRequestConfig & { errorHandle?: boolean };

const k = (key: string) => encodeURIComponent(key);

const fetchPlaybooks = async (includeArchived: boolean): Promise<PlaybookSummary[]> => {
    const { data } = await axios.get<PlaybookSummary[]>(
        `${apiUrl}/Lens/Playbooks?includeArchived=${includeArchived}`,
    );
    return data;
};

const fetchPlaybook = async (key: string, quiet = false): Promise<Playbook> => {
    const { data } = await axios.get<Playbook>(`${apiUrl}/Lens/Playbook/${k(key)}`, quiet ? noSnack : undefined);
    return data;
};

const fetchChangelog = async (key: string): Promise<PlaybookChange[]> => {
    const { data } = await axios.get<PlaybookChange[]>(`${apiUrl}/Lens/Changelog/${k(key)}`);
    return data;
};

const createPlaybook = async ({ key, displayName }: { key: string; displayName: string }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/CreatePlaybook`, { key, displayName });
    return data;
};

const updatePlaybook = async ({
    key,
    changes,
}: {
    key: string;
    changes: { displayName?: string; enabled?: boolean; archived?: boolean };
}): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/UpdatePlaybook/${k(key)}`, changes);
    return data;
};

const createProvision = async ({ key, provision }: { key: string; provision: SaveProvision }): Promise<Provision> => {
    const { data } = await axios.post<Provision>(`${apiUrl}/Lens/CreateProvision/${k(key)}`, provision);
    return data;
};

const saveProvision = async ({ key, provision }: { key: string; provision: SaveProvision }): Promise<Provision> => {
    const { data } = await axios.post<Provision>(
        `${apiUrl}/Lens/SaveProvision/${k(key)}/${k(provision.key)}`,
        provision,
    );
    return data;
};

const archiveProvision = async ({ key, provisionKey }: { key: string; provisionKey: string }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/ArchiveProvision/${k(key)}/${k(provisionKey)}`);
    return data;
};

const restoreProvision = async ({ key, provisionKey }: { key: string; provisionKey: string }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/RestoreProvision/${k(key)}/${k(provisionKey)}`);
    return data;
};

const approveAll = async (key: string): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/ApproveAll/${k(key)}`);
    return data;
};

const createGroup = async ({ key, name }: { key: string; name: string }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/CreateGroup/${k(key)}`, { name });
    return data;
};

const renameGroup = async ({ key, id, name }: { key: string; id: number; name: string }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/RenameGroup/${k(key)}/${id}`, { name });
    return data;
};

const deleteGroup = async ({ key, id }: { key: string; id: number }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/DeleteGroup/${k(key)}/${id}`);
    return data;
};

const arrangeProvisions = async ({ key, arrangement }: { key: string; arrangement: Arrangement }): Promise<Playbook> => {
    const { data } = await axios.post<Playbook>(`${apiUrl}/Lens/ArrangeProvisions/${k(key)}`, arrangement);
    return data;
};

const generatePlaybook = async (request: GenerateRequest): Promise<GenerateResult> => {
    const { data } = await axios.post<GenerateResult>(`${apiUrl}/Lens/GeneratePlaybook`, request, noSnack);
    return data;
};

const draftFromPrecedent = async ({ key, text }: { key: string; text: string }): Promise<GenerateResult> => {
    const { data } = await axios.post<GenerateResult>(
        `${apiUrl}/Lens/DraftFromPrecedent/${k(key)}`,
        { text },
        noSnack,
    );
    return data;
};

const retryGeneration = async (key: string): Promise<GenerateResult> => {
    const { data } = await axios.post<GenerateResult>(`${apiUrl}/Lens/RetryGeneration/${k(key)}`, null, noSnack);
    return data;
};

const r = (identifier: string) => encodeURIComponent(identifier);

const fetchRuns = async (): Promise<RunSummary[]> => {
    const { data } = await axios.get<RunSummary[]>(`${apiUrl}/Lens/Runs`);
    return data;
};

const fetchRun = async (identifier: string, quiet = false): Promise<Run> => {
    const { data } = await axios.get<Run>(`${apiUrl}/Lens/Run/${r(identifier)}`, quiet ? noSnack : undefined);
    return data;
};

const fetchRunDocument = async (identifier: string): Promise<RunDocument> => {
    const { data } = await axios.get<RunDocument>(`${apiUrl}/Lens/RunDocument/${r(identifier)}`);
    return data;
};

const createRun = async (request: CreateRun): Promise<Run> => {
    const { data } = await axios.post<Run>(`${apiUrl}/Lens/CreateRun`, request, noSnack);
    return data;
};

const decide = async ({
    identifier,
    findingKey,
    decision,
}: {
    identifier: string;
    findingKey: string;
    decision: FindingDecision;
}): Promise<Run> => {
    const { data } = await axios.post<Run>(`${apiUrl}/Lens/Decide/${r(identifier)}`, { findingKey, decision });
    return data;
};

const generateRedline = async (identifier: string): Promise<Run> => {
    const { data } = await axios.post<Run>(`${apiUrl}/Lens/GenerateRedline/${r(identifier)}`, null, noSnack);
    return data;
};

const archiveRun = async (identifier: string): Promise<void> => {
    await axios.post(`${apiUrl}/Lens/ArchiveRun/${r(identifier)}`);
};

// A blob error body cannot be read by the global snack, so the caller reports a failed download itself.
const download = async (url: string, fileName: string) => {
    const { data } = await axios.get<Blob>(url, { ...noSnack, responseType: "blob" });
    saveAs(data, fileName);
};

const downloadOriginal = (run: Pick<RunSummary, "identifier" | "documentName">) =>
    download(`${apiUrl}/Lens/Original/${r(run.identifier)}`, run.documentName);

const downloadMarkedUp = (run: Pick<RunSummary, "identifier" | "documentName">) =>
    download(
        `${apiUrl}/Lens/MarkedUp/${r(run.identifier)}`,
        `${run.documentName.replace(/\.docx$/i, "")} (marked up).docx`,
    );

const downloadPlaybookSource = (key: string, id: number, fileName: string) =>
    download(`${apiUrl}/Lens/PlaybookSource/${k(key)}/${id}`, fileName);

const lensService = {
    fetchPlaybooks,
    fetchPlaybook,
    fetchChangelog,
    createPlaybook,
    updatePlaybook,
    createProvision,
    saveProvision,
    archiveProvision,
    restoreProvision,
    approveAll,
    createGroup,
    renameGroup,
    deleteGroup,
    arrangeProvisions,
    generatePlaybook,
    draftFromPrecedent,
    retryGeneration,
    fetchRuns,
    fetchRun,
    fetchRunDocument,
    createRun,
    decide,
    generateRedline,
    archiveRun,
    downloadOriginal,
    downloadMarkedUp,
    downloadPlaybookSource,
};

export default lensService;
